import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Button, Input } from '@pijma/crypto'
import { useBaseMask } from '@pijma/input'

import { Dispatch, RootState, store } from '@stores'
import { ECurrency, isoToECurrency, TPayFormState } from '@stores/types/*'
import {
  charIsUzLetterWithSpaces,
  removeDoubleSpaces,
  replaceNotUzSymbols,
} from '@utils'
import styled from 'styled-components'

import { SESSION_SENDER_FULL_NAME_KEY } from '../../../../../stores/payform/payForm'
import { EPaymentProvider } from '../../../../../stores/types/EPaymentProvider'
import { TPaymentGateway } from '../../../../../stores/types/TPaymentGatewayState'
import { H4 } from '../../../../common/header/Headings'
import { Island } from '../../../../common/island'
import NavigationBar from '../../../../common/navigation/NavigationBar'
import { BodyText500, CaptionText500 } from '../../../../common/text/BodyText'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const NavigationBarWrapper = styled.div`
  padding: 0 24px;
  display: flex;
  width: 100%;
  align-items: start;
`

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px 24px;
`

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 8px 20px 20px;
`

const SourceTitle = styled.div`
  display: flex;
  flex-direction: column;
  white-space: pre-wrap;
  width: 100%;
  padding: 20px 24px 16px;
  gap: 4px;
`

const BackContainer = styled.div`
  margin: 24px 0 0 0;
  color: #217aac;

  span {
    cursor: pointer;

    :hover {
      color: rgb(49, 49, 49);
    }
  }
`

interface StateProps {
  payForm: TPayFormState
  paymentGateway: TPaymentGateway | undefined
}

interface DispatchProps {
  setSenderFirstName: (senderFirstName: string) => void
  setSenderPatronymic: (senderPatronymic: string) => void
  setSenderLastName: (senderLastName: string) => void
  doPaymentBlocking: () => void
  resetPaymentBlocking: () => void
}

type Props = StateProps & DispatchProps

const PurchaseSourceCardPage = (props: Props) => {
  const intl = useIntl()
  const redirectTo = useNavigate()

  const [formValid, setFormValid] = useState<boolean>(false)

  useEffect(() => {
    setFormValid(
      props.payForm.senderFullName?.lastName !== undefined &&
        props.payForm.senderFullName?.firstName !== undefined &&
        props.payForm.senderFullName?.lastName !== '' &&
        props.payForm.senderFullName?.firstName !== '',
    )
  }, [
    props.payForm.senderFullName?.firstName,
    props.payForm.senderFullName?.lastName,
  ])

  const formatSender = (val: string) => {
    val = replaceNotUzSymbols(val)
    val = removeDoubleSpaces(val)
    return val
  }

  const senderLastNameMask = useBaseMask({
    type: 'text',
    format: formatSender,
    isValidInputCharacter: charIsUzLetterWithSpaces,
    removeFormatting: replaceNotUzSymbols,
    value: props.payForm.senderFullName?.lastName,
    onValueChange: ({ formattedValue }) => {
      props.setSenderLastName(formattedValue.trim())
    },
  })

  const senderFirstNameMask = useBaseMask({
    type: 'text',
    format: formatSender,
    isValidInputCharacter: charIsUzLetterWithSpaces,
    removeFormatting: replaceNotUzSymbols,
    value: props.payForm.senderFullName?.firstName,
    onValueChange: ({ formattedValue }) => {
      props.setSenderFirstName(formattedValue.trim())
    },
  })

  const senderPatronymicMask = useBaseMask({
    type: 'text',
    format: formatSender,
    isValidInputCharacter: charIsUzLetterWithSpaces,
    removeFormatting: replaceNotUzSymbols,
    value: props.payForm.senderFullName?.patronymic,
    onValueChange: ({ formattedValue }) => {
      props.setSenderPatronymic(formattedValue.trim())
    },
  })

  const goToSumPage = () => {
    if (props.payForm.senderFullName) {
      sessionStorage.setItem(
        SESSION_SENDER_FULL_NAME_KEY,
        JSON.stringify(props.payForm.senderFullName),
      )
    }
    redirectTo(`../../sum`)
  }

  useEffect(() => {
    // skip personal info input for VISA purchase
    if (
      isoToECurrency(props.paymentGateway?.currencyFrom) == ECurrency.USD &&
      props.paymentGateway?.provider == EPaymentProvider.PW
    ) {
      goToSumPage()
    }
  }, [props.paymentGateway])

  return (
    <Container>
      <NavigationBarWrapper>
        <NavigationBar
          paymentMethodRedirectLink={'../../source-select'}
          paymentDisabled
          sumDisabled
          noPaymentMethod={true}
        />
      </NavigationBarWrapper>
      <Island size="M" bgColor="primary">
        <SourceTitle data-testid="userdata.header">
          <H4>
            {intl.formatMessage({ id: 'purchaseSourceUzCardPage.title' })}
          </H4>
          <CaptionText500>
            {intl.formatMessage({ id: 'purchaseSourceUzCardPage.subtitle' })}
          </CaptionText500>
        </SourceTitle>
        <InputContainer>
          <Input
            {...senderLastNameMask}
            label={intl.formatMessage({
              id: 'purchaseSourceUzCardPage.lastNameLabel',
            })}
            placeholder={intl.formatMessage({
              id: 'purchaseSourceUzCardPage.lastNamePlaceholder',
            })}
            description={intl.formatMessage({
              id: 'purchaseSourceUzCardPage.lastNameDescription',
            })}
            width={'100%'}
            disabled={props.payForm.paymentBlocking}
            type={'text'}
            allowClear={true}
            autoFocus={true}
          />
        </InputContainer>
        <InputContainer>
          <Input
            {...senderFirstNameMask}
            label={intl.formatMessage({
              id: 'purchaseSourceUzCardPage.firstNameLabel',
            })}
            width={'100%'}
            disabled={props.payForm.paymentBlocking}
            type={'text'}
            allowClear={true}
          />
        </InputContainer>
        <InputContainer>
          <Input
            {...senderPatronymicMask}
            label={intl.formatMessage({
              id: 'purchaseSourceUzCardPage.patronymicLabel',
            })}
            description={intl.formatMessage({
              id: 'purchaseSourceUzCardPage.patronymicDescription',
            })}
            width={'100%'}
            disabled={props.payForm.paymentBlocking}
            type={'text'}
            allowClear={true}
          />
        </InputContainer>
        <ButtonContainer>
          <Button
            width={'100%'}
            size={'m'}
            disabled={!formValid}
            onPress={() => goToSumPage()}
            data-testid="userdata.button.forward"
          >
            {intl.formatMessage({ id: 'purchaseSourceSbpPage.goToSumPage' })}
          </Button>
        </ButtonContainer>
      </Island>
      <BackContainer
        onClick={() => redirectTo('../../provider-select')}
        data-testid="payment-methods.back"
      >
        <BodyText500>
          {intl.formatMessage({ id: 'purchaseSourceSelectPage.back' })}
        </BodyText500>
      </BackContainer>
    </Container>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  payForm: state.payForm,
  paymentGateway: state.payForm?.paymentGateway,
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    setSenderLastName: (senderLastName: string) => {
      const current = store.getState().payForm.senderFullName

      dispatch.payForm.set({
        senderFullName: {
          firstName: current?.firstName ?? '',
          patronymic: current?.patronymic,
          lastName: senderLastName,
        },
      })
    },
    setSenderFirstName: (senderFirstName: string) => {
      const current = store.getState().payForm.senderFullName

      dispatch.payForm.set({
        senderFullName: {
          firstName: senderFirstName,
          patronymic: current?.patronymic,
          lastName: current?.lastName ?? '',
        },
      })
    },
    setSenderPatronymic: (senderPatronymic: string) => {
      const current = store.getState().payForm.senderFullName
      dispatch.payForm.set({
        senderFullName: {
          firstName: current?.firstName ?? '',
          patronymic: senderPatronymic,
          lastName: current?.lastName ?? '',
        },
      })
    },
    doPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: true,
      }),
    resetPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: false,
      }),
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PurchaseSourceCardPage)
